import jsPDF from "jspdf";
import "jspdf-autotable";
import "../../../../assets/font/THSarabunNew-normal";
import "../../../../assets/font/THSarabunNew-bold";
import CurrencyFormat from "../../../../hook/CurrencyFormat";

import arabicToThai from "../../../../hook/arabicToThai";
import dayjs from "dayjs";
import JsBarcode from "jsbarcode";

const [
  currencyFormat,
  currencyFormatComma,
  currencyFormatPoint,
  currencyFormatNoPoint,
] = CurrencyFormat();

const [convertToThaiNumerals, toThaiWord, numberToThaiText] = arabicToThai();

// แปลงค่าใด ๆ → number (รองรับ number / string มี comma / null / undefined)
const toNumber = (val) => {
  if (val === null || val === undefined || val === "") return 0;
  if (typeof val === "number") return val;
  const n = Number(String(val).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

// แปลงค่าใด ๆ → string format เงิน เช่น "17,000.00" (รับประกันว่าได้ string เสมอ)
const toMoney = (val) =>
  toNumber(val)
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, "$&,");

// ─── Constants ────────────────────────────────────────────────────
const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN_X = 15;
const CONTENT_W = PAGE_W - 2 * MARGIN_X;
const FONT = "THSarabunNew";

// แยกที่อยู่ที่ " ต." (ตำบล) เพื่อขึ้นบรรทัด 2
const splitAddress = (addr) => {
  if (!addr) return ["", ""];
  const m = addr.match(/^(.*?)\s+(ต\..*)$/);
  if (m) return [m[1].trim(), m[2].trim()];
  return [addr, ""];
};

// ─── Drawing helpers ──────────────────────────────────────────────
const setFont = (doc, weight = "normal", size = 10) => {
  doc.setFont(FONT, weight);
  doc.setFontSize(size);
};

// =================================================================
// ─── Barcode / QR helpers (แปลงข้อมูล → รูปภาพ) ──────────────────
// =================================================================
const QRCODE_URL =
  "https://asia-east2-fir-express-c1aec.cloudfunctions.net/aa/loans";

// barcode: สร้าง sync ในเครื่องด้วย JsBarcode → dataURL
const genBarcode = (value, opts = {}) => {
  if (!value) return null;
  try {
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, String(value), {
      format: "CODE128",
      displayValue: false, // true = โชว์ตัวเลขใต้บาร์โค้ด
      margin: 0,
      height: 60,
      width: 2,
      ...opts,
    });
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
};

// QR (รูปจาก URL): โหลด → canvas → dataURL (async, ต้องการ CORS)
const loadImageAsDataURL = (url) =>
  new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous"; // endpoint ต้องส่ง CORS header ด้วย
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        c.getContext("2d").drawImage(img, 0, 0);
        resolve(c.toDataURL("image/png"));
      } catch {
        resolve(null); // canvas tainted (CORS) → ข้าม
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

// เตรียมรูป barcode + QR (เรียกก่อนวาดหน้า 3)
const prepareCodes = async (data = {}) => {
  const ref1 = "001";
  let ref2 = String(data.contract_no || "");
  ref2 = ref2.replace(/^[A-Za-z]/, "86").replace(/-/g, "");
  console.log("data = {}", data);
  console.log("data = {}", data.sold_out_price);

  const billerBank = "0405559001925";
  const billerCtr = "0405552000435";
  const amt = Math.round(data.sold_out_price * 100); // หน่วยสตางค์

  const qrBankUrl = `${QRCODE_URL}/qr/${billerBank}/00/${ref1}/${ref2}/${amt}`;
  const qrCounterUrl = `${QRCODE_URL}/qr/${billerCtr}/00/${ref1}/${ref2}/${amt}`;
  const barcodeValue = `|${billerBank}\r${ref1}\r${ref2}\r${amt}`;

  const [qrBankImg, qrCounterImg] = await Promise.all([
    loadImageAsDataURL(qrBankUrl),
    loadImageAsDataURL(qrCounterUrl),
  ]);
  const barcodeImg = genBarcode(barcodeValue);

  return { barcodeImg, qrBankImg, qrCounterImg, barcodeValue, ref1, ref2 };
};

// =================================================================
// ─── Section: Tax Invoice / Receipt ───────────────────────────────
// =================================================================
const drawInvoiceSection = (
  doc,
  data,
  editData,
  startY,
  isTaxInvoice,
  companyInvoice,
  record,
) => {
  let y = startY;
  const left = MARGIN_X;
  const right = PAGE_W - MARGIN_X;
  const midX = PAGE_W / 2;

  // ============================================================
  // ── Header: Company (left) + Title box (right) ──
  // ============================================================

  setFont(doc, "bold", 13);
  doc.text(companyInvoice.label || "", left, y + 5);

  // Title box (ขวาบน)
  const titleText = isTaxInvoice ? "ใบกำกับภาษี" : "ใบเสร็จรับเงิน";
  const titleBoxW = 40;
  const titleBoxH = 9;
  const titleBoxX = right - titleBoxW;
  doc.rect(titleBoxX - 10, y + 1, titleBoxW, titleBoxH - 2, "D");
  setFont(doc, "bold", 16);
  doc.text(titleText, titleBoxX - 10 + titleBoxW / 2, y + 6, {
    align: "center",
  });

  y += 13;

  // Company address (ซ้าย)
  setFont(doc, "bold", 14);
  doc.text(`ที่อยู่ ${companyInvoice.data.address || ""}`, left, y);
  doc.text(
    `เลขที่ใบกำกับ  ${editData.doc_no || editData.invoice_no || data.invoice_no || ""}`,
    titleBoxX - 10,
    y,
  );
  y += 5;
  doc.text(
    `วันที่ใบกำกับ  ${dayjs(editData.doc_dt).format("DD/MM/YYYY")}`,
    titleBoxX - 10,
    y,
  );
  y += 3;

  // ============================================================
  // ── Customer Info Box (มี border + เส้นแบ่งกลาง) ──
  // ============================================================

  // -- ขวา: เลขที่สัญญา + เลขผู้เสียภาษีบริษัท --
  let yr = y;
  doc.text(`เลขที่สัญญา  ${record.CONTNO || ""}`, titleBoxX - 10, yr + 2);
  yr += 7;
  doc.text("เลขประจำตัวผู้เสียภาษีอากร :", titleBoxX - 10, yr);
  yr += 5;
  doc.text(companyInvoice.data.tax_id || "", titleBoxX - 10, yr);

  const custBoxH = 24;
  const custDivX = right - 69; // เส้นแบ่งกลาง: ซ้าย = ลูกค้า, ขวา = สัญญา+เลขผู้เสียภาษี

  doc.rect(left, y, CONTENT_W - 69, custBoxH, "D");
  doc.line(custDivX, y, custDivX, y + custBoxH);

  // -- ซ้าย: ข้อมูลลูกค้า --
  setFont(doc, "bold", 14);
  let cy = y + 5;
  doc.text(`ชื่อลูกค้า  ${editData.buyer_name || ""}`, left + 2, cy, {
    maxWidth: custDivX - left - 4,
  });
  cy += 5;
  doc.text(
    `เลขประจำตัวผู้เสียภาษีอากร  ${editData.buyer_taxid || ""}`,
    left + 2,
    cy,
  );
  cy += 5;

  const [addr1, addr2] = splitAddress(editData.buyer_address || "");
  doc.text(`บ้านเลขที่  ${addr1}`, left + 2, cy);
  cy += 5;
  if (addr2) {
    doc.text(addr2, left + 15, cy);
  }

  y += custBoxH;

  // ============================================================
  // ── Table ──
  // ============================================================
  const colW = [16, 95, 32, 37];
  const totalW = colW.reduce((s, c) => s + c, 0);
  const scale = CONTENT_W / totalW;
  const w = colW.map((c) => c * scale);
  const colX = [left];
  w.slice(0, -1).forEach((cw, i) => colX.push(colX[i] + cw));

  // Header row
  const headerH = 7;
  setFont(doc, "bold", 14);
  doc.rect(left, y, CONTENT_W, headerH, "D");
  doc.line(colX[1], y, colX[1], y + headerH);
  doc.line(colX[2], y, colX[2], y + headerH);
  doc.line(colX[3], y, colX[3], y + headerH);
  doc.text("ลำดับที่", colX[0] + w[0] / 2, y + 5, { align: "center" });
  doc.text("รายการ", colX[1] + w[1] / 2, y + 5, { align: "center" });
  doc.text("ราคาต่อหน่วย", colX[2] + w[2] / 2, y + 5, { align: "center" });
  doc.text("จำนวนเงิน", colX[3] + w[3] / 2, y + 5, { align: "center" });
  y += headerH;
  console.log("editData.sellAmount", editData.sellAmount);
  console.log(toNumber(editData.sellAmount));
  console.log(toNumber(editData.sold_out_price) / 1.07);

  // Item row (4 บรรทัด)
  const lineAmount = isTaxInvoice
    ? toMoney(
        toNumber(editData.sellAmount) ||
          toNumber(editData.sold_out_price) / 1.07,
      )
    : toMoney(editData.sold_out_price || data.sold_out_price);
  console.log("lineAmount", lineAmount);

  const itemRowH = 22;
  doc.rect(left, y, CONTENT_W, itemRowH, "D");
  doc.line(colX[1], y, colX[1], y + itemRowH);
  doc.line(colX[2], y, colX[2], y + itemRowH);
  doc.line(colX[3], y, colX[3], y + itemRowH);

  setFont(doc, "normal", 14);
  doc.text("1", colX[0] + w[0] / 2, y + 5, { align: "center" });

  const itemX = colX[1] + 2;
  doc.text("ขายทอดตลาด", itemX, y + 5);
  doc.text(
    `ยี่ห้อ ${data.brand || editData.TYPE || ""} รุ่น ${data.model || editData.MODEL || ""} สี ${data.color || editData.COLOR || ""}`,
    itemX,
    y + 10,
  );
  doc.text(
    `เลขเครื่อง  ${data.engine_no || editData.ENGNO || ""}`,
    itemX,
    y + 15,
  );
  doc.text(
    `เลขตัวถัง  ${editData.STRNO || data.chassis_no || ""}`,
    itemX,
    y + 20,
  );

  doc.text(lineAmount, colX[2] + w[2] - 2, y + 5, { align: "right" });
  doc.text(lineAmount, right - 2, y + 5, { align: "right" });
  y += itemRowH;

  // ============================================================
  // ── (ตัวอักษร) ซ้าย + Totals ขวา ที่ Y เดียวกัน ──
  // ============================================================
  const thaiText = numberToThaiText(toNumber(editData.sold_out_price));
  const rowH = 6;

  // ซ้าย: 2 cell — label + thai text (กว้างเท่า col 1 + col 2)
  const labelCellW = w[0] + w[1] * 0.42;
  const textCellW = w[1] * 0.58;
  doc.rect(left, y, labelCellW - 11, rowH + 4, "D");
  doc.rect(left + labelCellW - 11, y, 66, rowH + 4, "D");
  setFont(doc, "bold", 12);
  doc.text("จำนวนเงินรวมทั้งสิ้น (ตัวอักษร)", left + 3, y + 7);
  const textString = thaiText || "";
  const maxAllowedWidth = 64;
  if (doc.getTextWidth(textString) > maxAllowedWidth) {
    setFont(doc, "normal", 10);
    doc.text(thaiText, left + labelCellW + textCellW / 2, y + 7, {
      align: "center",
    });
  } else {
    setFont(doc, "normal", 12);
    doc.text(thaiText, left + labelCellW + textCellW / 2, y + 7, {
      align: "center",
    });
  }

  // ขวา: totals (col 3 + col 4)
  let totalY = y;
  const drawTotalRow = (label, value, bold = false) => {
    console.log("label", label, value);
    console.log("colX", colX[2]);

    doc.rect(colX[2], totalY, w[2], rowH + 4, "D");
    doc.rect(colX[3], totalY, w[3], rowH + 4, "D");
    setFont(doc, bold ? "bold" : "normal", 14);
    doc.text(label, colX[2] + w[2] - 2, totalY + 7, { align: "right" });
    doc.text(value, right - 2, totalY + 7, { align: "right" });
    totalY += rowH + 4;
  };

  if (isTaxInvoice) {
    drawTotalRow("มูลค่าสินค้า", toMoney(editData.sellAmount));
    drawTotalRow("ภาษีมูลค่าเพิ่ม 7%", toMoney(editData.vat));
    drawTotalRow("ราคารวมภาษี", toMoney(editData.sold_out_price), true);
  } else {
    drawTotalRow("รวมมูลค่าสินค้า", toMoney(editData.sold_out_price), true);
  }

  y = Math.max(y + rowH, totalY);
  y += 20;

  // ============================================================
  // ── Signature ──
  // ============================================================
  setFont(doc, "bold", 14);
  doc.text(
    "ลงชื่อ.........................................ผู้รับเงิน",
    left + 5,
    y,
  );
  doc.text(
    "ลงชื่อ.........................................ผู้จัดการ",
    midX + 10,
    y,
  );

  return y;
};

// =================================================================
// ─── Page 2: Cash Bill ────────────────────────────────────────────
// =================================================================
const drawCashBill = (
  doc,
  data,
  editData = {},
  companyReceipt = {},
  record,
) => {
  console.log("doc", doc);
  console.log("data", data);
  console.log("editData", editData);
  const left = MARGIN_X;
  const right = PAGE_W - MARGIN_X;
  const midX = PAGE_W / 2;
  let y = 15;

  // ── Header ──
  setFont(doc, "bold", 16);
  doc.text(companyReceipt.label || "", left, y);
  doc.text("บิลเงินสด", right, y, { align: "right" });

  y += 6;
  setFont(doc, "bold", 14);
  doc.text(companyReceipt.data.address || "", left, y);

  doc.text(
    `เลขที่ใบกำกับ ${editData.doc_no || editData.invoice_no || data.invoice_no || ""}`,
    right,
    y,
    { align: "right" },
  );

  y += 5;
  doc.text(
    `เลขประจำตัวผู้เสียภาษี   ${companyReceipt.data.tax_id || ""}`,
    left,
    y,
  );
  doc.text(
    `วันที่   ${dayjs(editData.doc_dt).format("DD/MM/YYYY")}`,
    right,
    y,
    { align: "right" },
  );
  y += 8;

  // ── Buyer Box + Tax Rate Box ──
  const buyerBoxW = CONTENT_W * 0.62;
  const taxBoxW = CONTENT_W - buyerBoxW - 4;
  const taxBoxX = left + buyerBoxW + 4;
  const boxH = 22;

  doc.rect(left, y + 1, buyerBoxW, boxH, "D");
  doc.text(`นามผู้ซื้อ  ${editData.buyer_name || ""}`, left + 2, y + 7, {
    maxWidth: buyerBoxW - 4,
  });
  doc.text("ที่อยู่", left + 2, y + 14);
  const addrLines = doc.splitTextToSize(
    editData.buyer_address || "",
    buyerBoxW - 18,
  );
  doc.text(addrLines, left + 13, y + 14);

  // Tax rate checkbox box
  const taxBoxH = 8;
  const taxBoxY = y + (boxH - taxBoxH) / 2;
  doc.rect(taxBoxX - 2, taxBoxY + 8, taxBoxW + 2, taxBoxH, "D");
  doc.text("อัตราภาษี", taxBoxX - 2, taxBoxY + 14);
  doc.rect(taxBoxX + 16, taxBoxY + 11, 3.5, 3.5, "D");
  doc.text("อัตราร้อยละ 7", taxBoxX + 21, taxBoxY + 14);
  doc.rect(taxBoxX + 45, taxBoxY + 11, 3.5, 3.5, "D");
  doc.text("อัตราศูนย์", taxBoxX + 50, taxBoxY + 14);

  if (toNumber(editData.vat) > 0) {
    setFont(doc, "bold", 22);
    doc.text("/", taxBoxX + 17, taxBoxY + 14);
  } else {
    setFont(doc, "bold", 22);
    doc.text("/", taxBoxX + 46, taxBoxY + 14);
  }

  y += boxH + 2;

  // ── Table ──
  const colW = [16, 95, 32, 37];
  const totalW = colW.reduce((s, c) => s + c, 0);
  const scale = CONTENT_W / totalW;
  const w = colW.map((c) => c * scale);
  const colX = [left];
  w.slice(0, -1).forEach((cw, i) => colX.push(colX[i] + cw));

  doc.line(left, y, right, y);
  y += 5;
  setFont(doc, "normal", 12);
  doc.text("ลำดับที่", colX[0] + w[0] / 2, y, { align: "center" });
  doc.text("รายการสินค้าหรือบริการ", colX[1] + w[1] / 2, y, {
    align: "center",
  });
  doc.text("ราคาต่อหน่วย", colX[2] + w[2] / 2, y, { align: "center" });
  doc.text("จำนวนเงิน (ไม่รวมภาษี)", colX[3] + w[3] / 2, y, {
    align: "center",
  });
  y += 2;
  doc.line(left, y, right, y);

  // Item row
  const subtotal = toMoney(editData.sellAmount || data.sold_out_price);
  y += 5;
  doc.text("1", colX[0] + w[0] / 2, y, { align: "center" });

  const itemX = colX[1] + 2;
  const itemMid = colX[1] + w[1] / 2 + 5;
  doc.text(`ยี่ห้อ  ${data.brand || editData.TYPE || ""}`, itemX, y);
  doc.text(`รุ่น (แบบ)  ${data.model || editData.MODEL || ""}`, itemMid, y);
  doc.text(subtotal, colX[2] + w[2] - 2, y, { align: "right" });
  doc.text(subtotal, right - 2, y, { align: "right" });

  y += 5;
  doc.text(`สี  ${data.color || ""}`, itemX, y);
  doc.text(`เลขเครื่อง  ${data.engine_no || ""}`, itemMid, y);

  y += 5;
  doc.text(`เลขตัวถัง  ${editData.STRNO || data.chassis_no || ""}`, itemX, y);

  y += 5;
  doc.line(left, y, right, y);

  // ── Totals (ขวา) + ตัวอักษร (ซ้าย) ──
  const totalsStartY = y;
  const labelX = colX[2] + w[2] - 2;
  const valueX = right - 2;

  const drawTotalRow = (label, value, underline = false) => {
    setFont(doc, "normal", 14);
    doc.text(label, labelX, y + 6, { align: "right" });
    doc.text(value, valueX, y + 6, { align: "right" });
    if (underline) doc.line(valueX - 25, y + 7, valueX + 2, y + 7);
    y += 6;
  };

  drawTotalRow("รวมราคาทั้งสิ้น", toMoney(editData.sellAmount), true);
  drawTotalRow("ภาษีมูลค่าเพิ่ม 7 %", toMoney(editData.vat), true);
  drawTotalRow("จำนวนเงินรวมทั้งสิ้น", toMoney(editData.sold_out_price), true);

  const thaiY = totalsStartY + 10;
  doc.text("จำนวนเงินรวม (ตัวอักษร)", left + 2, thaiY);
  const thaiText = numberToThaiText(toNumber(editData.sold_out_price));
  const thaiBoxX = left + 38;
  const thaiBoxW = labelX - 40 - thaiBoxX;
  doc.text(thaiText, thaiBoxX + thaiBoxW / 2, thaiY, { align: "center" });
  doc.line(thaiBoxX, thaiY + 1, thaiBoxX + thaiBoxW, thaiY + 1);

  y += 10;

  // ── Signatures ──
  const sigY = y + 5;
  const receiver =
    editData.receiver_name && editData.receiver_name.trim()
      ? editData.receiver_name
      : "............................";

  doc.text(`ลงชื่อ  ${receiver}  ผู้รับเงิน`, left + 10, sigY);
  doc.text(
    "ลงชื่อ............................................ผู้จัดการ",
    midX + 10,
    sigY,
  );
  doc.text(
    "(............................................)",
    left + 18,
    sigY + 8,
  );
  doc.text(
    "(............................................)",
    midX + 18,
    sigY + 8,
  );
};

// =================================================================
// ─── finaceBill : ใบรับเงิน
// =================================================================

const DEFAULT_RECEIVER = "น.ส.กานดาวศรี แพนจำปา";

const money = (val) => {
  const n = toNumber(val);
  return n ? toMoney(n) : "";
};

const drawCheckItem = (doc, x, y, label, size = 3) => {
  doc.rect(x, y - size + 0.3, size, size, "D");
  doc.text(label, x + size + 2, y);
};

// เส้นประคั่นส่วน
const dashedLine = (doc, y) => {
  doc.setLineDashPattern([1.2, 1.2], 0);
  doc.line(MARGIN_X, y, PAGE_W - MARGIN_X, y);
  doc.setLineDashPattern([], 0);
};

// =================================================================
// ─── ส่วนที่ 1 : ใบรับเงิน (หัว + ตาราง) ─────────────────────────
// =================================================================
const drawReceiptSection = (
  doc,
  data,
  editData,
  startY,
  companyReceipt,
  record,
) => {
  const left = MARGIN_X; // 15
  const right = PAGE_W - MARGIN_X; // 195
  let y = startY;

  console.log("drawReceiptSection data", data);
  console.log("drawReceiptSection editData", editData);
  console.log("drawReceiptSection record", record);

  const receiver =
    editData.receiver_name && editData.receiver_name.trim()
      ? editData.receiver_name
      : DEFAULT_RECEIVER;

  // ── Header ──
  setFont(doc, "bold", 14);
  doc.text(companyReceipt.label || "", left, y + 4);
  setFont(doc, "bold", 16);
  doc.text("ใบรับเงิน", PAGE_W / 2 + 5, y + 4, { align: "center" });

  y += 9;
  setFont(doc, "normal", 14);
  doc.text(companyReceipt.data.address || "", left, y);
  doc.text(
    `เลขที่ใบรับ  ${editData.doc_no || editData.invoice_no || data.invoice_no || ""}`,
    right,
    y,
    { align: "right" },
  );

  y += 5;
  doc.text(`เบอร์โทร ${companyReceipt.data.phone || ""}`, left, y);
  doc.text(
    `วันที่รับ   ${dayjs(editData.doc_dt).format("DD/MM/YYYY")}`,
    right,
    y,
    { align: "right" },
  );

  y += 5;
  doc.text(`ชื่อลูกค้า : ${editData.buyer_name || ""}`, left, y);
  doc.text(`เลขสัญญา : ${record?.CONTNO || ""}`, right, y, { align: "right" });

  // ที่อยู่ลูกค้า 2 บรรทัด (ใช้ splitAddress เหมือน drawInvoiceSection)
  const [addr1, addr2] = splitAddress(editData.buyer_address || "");
  y += 5;
  doc.text(`ที่อยู่ : ${addr1}`, left, y);
  doc.text(
    `ยี่ห้อ ${data.brand || editData.TYPE || ""} รุ่น ${data.model || editData.MODEL || ""} สี ${data.color || editData.COLOR || ""}`,
    right,
    y,
    { align: "right" },
  );

  y += 5;
  if (addr2) doc.text(addr2, left + 12, y);
  doc.text(
    `เลขตัวถัง ${data.STRNO || editData.chassis_no || ""} ทะเบียน ${data.regno || record?.REGNO || editData.REGNO || ""}`,
    right,
    y,
    { align: "right" },
  );

  y += 5;
  doc.text(`ชำระโดย : ${editData.pay_type || "เงินสด"}`, left, y);

  y += 3;

  // ── ตาราง ──
  const cols = [
    { label: "ลำดับ", w: 14 },
    { label: "ชำระค่า", w: 46 },
    { label: "หักลูกหนี้", w: 24 },
    { label: "ส่วนลด", w: 24 },
    { label: "ค่าปรับ", w: 24 },
    { label: "ลดค่าปรับ", w: 24 },
    { label: "รับสุทธิ", w: 24 },
  ];
  const tableW = cols.reduce((s, c) => s + c.w, 0); // 180
  const colX = [left];
  cols.forEach((c, i) => {
    if (i < cols.length - 1) colX.push(colX[i] + c.w);
  });

  // header row
  const headH = 7;
  setFont(doc, "bold", 13);
  doc.rect(left, y, tableW, headH, "D");
  cols.forEach((c, i) => {
    if (i > 0) doc.line(colX[i], y, colX[i], y + headH);
    doc.text(c.label, colX[i] + c.w / 2, y + 5, { align: "center" });
  });
  y += headH;

  // data row — ปรับ field ให้ตรงข้อมูลจริงได้
  const amount = toNumber(editData.sold_out_price || data.sold_out_price);
  const rowH = 18;
  doc.rect(left, y, tableW, rowH, "D");
  cols.forEach((c, i) => {
    if (i > 0) doc.line(colX[i], y, colX[i], y + rowH);
  });
  setFont(doc, "normal", 13);
  const ty = y + 6;
  doc.text("1", colX[0] + cols[0].w / 2, ty, { align: "center" });
  doc.text(editData.pay_desc || "รับชำระค่ารถเงินสด", colX[1] + 2, ty, {
    maxWidth: cols[1].w - 4,
  });
  doc.text(money(amount), colX[2] + cols[2].w - 2, ty, { align: "right" }); // หักลูกหนี้
  doc.text(money(editData.discount), colX[3] + cols[3].w - 2, ty, {
    align: "right",
  }); // ส่วนลด
  doc.text(money(editData.fine), colX[4] + cols[4].w - 2, ty, {
    align: "right",
  }); // ค่าปรับ
  doc.text(money(editData.fine_discount), colX[5] + cols[5].w - 2, ty, {
    align: "right",
  }); // ลดค่าปรับ
  doc.text(money(amount), colX[6] + cols[6].w - 2, ty, { align: "right" }); // รับสุทธิ
  y += rowH;

  // footer row : ตัวอักษร + จำนวนเงิน
  const footH = 9;
  const c1w = 40;
  const c3w = 24;
  const c4w = 24;
  const c2w = tableW - c1w - c3w - c4w;
  const fx1 = left;
  const fx2 = left + c1w;
  const fx3 = right - c3w - c4w;
  const fx4 = right - c4w;
  doc.rect(fx1, y, c1w, footH, "D");
  doc.rect(fx2, y, c2w, footH, "D");
  doc.rect(fx3, y, c3w, footH, "D");
  doc.rect(fx4, y, c4w, footH, "D");
  doc.text("จำนวนเงินเป็นตัวอักษร", fx1 + c1w / 2, y + 6, { align: "center" });
  doc.text(numberToThaiText(amount) || "", fx2 + c2w / 2, y + 6, {
    align: "center",
  });
  doc.text("จำนวนเงิน", fx3 + c3w / 2, y + 6, { align: "center" });
  doc.text(money(amount), fx4 + c4w - 2, y + 6, { align: "right" });
  y += footH + 5;

  // ── ค้างชำระ + ผู้รับเงิน ──
  setFont(doc, "normal", 12);
  const ovdTerms = editData.ovdue_terms ?? 0;
  const ovdAmt = toMoney(editData.ovdue_amt ?? 0);
  const ovdFrom = editData.ovdue_from ?? 0;
  const ovdTo = editData.ovdue_to ?? 0;
  const ovdInt = toMoney(editData.ovdue_int ?? 0);
  doc.text(
    `ค้างชำระ ${ovdTerms} งวด เป็นเงิน ${ovdAmt} บาท จากงวดที่ ${ovdFrom} ถึงงวดที่ ${ovdTo} ค้างดอกเบี้ยล่าช้า ${ovdInt} บาท`,
    left,
    y,
  );
  doc.text(`ผู้รับเงิน ${receiver}`, right, y, { align: "right" });

  y += 5;
  doc.text(
    "ใบเสร็จรับเงินฉบับนี้จะสมบูรณ์เมื่อปรากฎลายเซ็นของผู้รับเงินครบถ้วนและเช็คของท่านผ่านบัญชีบริษัทฯเรียบร้อยแล้ว",
    left,
    y,
    { maxWidth: 150 },
  );
  doc.text("( ออกโดยระบบอัตโนมัติ )", right, y, { align: "right" });

  return y + 6;
};

// =================================================================
// ─── ส่วนที่ 2/3 : สลิป Bill Payment (ลูกค้า / ธนาคาร) ───────────
// =================================================================
const drawPayInSlip = (doc, startY, ctx) => {
  const { data, editData, companyReceipt, record, sideLabel } = ctx;
  const left = MARGIN_X;
  const right = PAGE_W - MARGIN_X;
  const boxX = right - 75; // กล่องข้อมูลลูกค้า (ขวา)
  const boxW = right - boxX;
  let y = startY;

  // หัวสลิป
  setFont(doc, "bold", 12);
  doc.text(
    "ใบนำฝากชำระเงินค่าสินค้าหรือบริการ (Bill Payment Pay-in-slip)",
    left,
    y,
  );
  doc.text(sideLabel, right, y, { align: "right" });
  y += 5;

  setFont(doc, "normal", 12);
  doc.text(companyReceipt.label || "", left, y);
  doc.text("โปรดเรียกเก็บค่าธรรมเนียมจากผู้ชำระเงิน", right, y, {
    align: "right",
  });
  y += 5;
  doc.text(
    `${companyReceipt.data.address || ""} โทร.${companyReceipt.data.phone || ""}`,
    left,
    y,
    { maxWidth: boxX - left - 3 },
  );

  // กล่องข้อมูลลูกค้า (ขวา)
  const boxTop = startY + 7;
  const boxH = 22;
  doc.rect(boxX, boxTop, boxW, boxH, "D");
  const headRowH = 7;
  doc.line(boxX, boxTop + headRowH, right, boxTop + headRowH);
  doc.line(boxX + boxW * 0.6, boxTop, boxX + boxW * 0.6, boxTop + headRowH);
  doc.text("สาขา.................", boxX + 2, boxTop + 5);
  doc.text("วันที่...........", boxX + boxW * 0.6 + 2, boxTop + 5);
  doc.text(`ชื่อลูกค้า  ${editData.buyer_name || ""}`, boxX + 2, boxTop + 12, {
    maxWidth: boxW - 4,
  });

  let ref2 = String(data.contract_no || "");
  ref2 = ref2.replace(/^[A-Za-z]/, "86").replace(/-/g, "");

  doc.text(`Customer No (Ref.1)   001`, boxX + 2, boxTop + 17);
  doc.text(`Customer No (Ref.2)   ${ref2 || ""}`, boxX + 2, boxTop + 21);

  // checkbox ธนาคาร (ซ้าย)
  y += 6;
  const banks = [
    "ธ.กสิกรไทย A/C No. (15/20)",
    "ธ.กรุงไทย A/C No. CODE : 6624 (15)",
    "ธ.ไทยพานิชย์ A/C No. (Bill Payment) (15/20)",
    // "เคาน์เตอร์เซอร์วิส",
    "เทสโก้โลตัส และ เทสโก้โลตัส เอ็กซ์เพรส",
  ];
  banks.forEach((b) => {
    drawCheckItem(doc, left, y, b);
    y += 5;
  });

  y = Math.max(y, boxTop + boxH) + 3;

  // แถบจำนวนเงิน (ขวา) + ข้อความเงินสด (ซ้าย)
  const cellH = 6;
  doc.text("รับชำระเป็นเงินสดเท่านั้น", left + 35, y + 4, { align: "center" });
  const aX = right - 95;
  const amtCells = [
    { l: "จำนวนเงิน/Amount", w: 35 },
    { l: "", w: 35 },
    { l: "บาท/Baht", w: 25 },
  ];
  let cx = aX;
  amtCells.forEach((c) => {
    doc.rect(cx, y, c.w, cellH, "D");
    if (c.l) doc.text(c.l, cx + 2, y + 4);
    cx += c.w;
  });
  y += cellH;

  // แถว Amount in Words
  doc.rect(aX - 20, y, 30, cellH, "D");
  doc.rect(aX + 10, y, 85, cellH, "D");
  doc.text("จำนวนเงินเป็นตัวอักษร", aX - 18, y + 4);
  y += cellH + 5;

  // By / Telephone (ซ้าย) + กล่องเจ้าหน้าที่ (ขวา)
  const offX = right - 70;
  doc.rect(offX, y - 4, 70, 16, "D");
  doc.text("สำหรับเจ้าหน้าที่ธนาคาร", offX + 2, y);
  doc.text("ผู้นำฝาก / Deposit By .................................", left, y);
  y += 6;
  doc.text(
    "ผู้รับเงิน ...........................................",
    offX + 2,
    y,
  );
  doc.text("โทรศัพท์ / Telephone ...............................", left, y);

  return y + 4;
};

// =================================================================
// ─── ช่อง barcode (กล่องรูปภาพล่างซ้าย) ──────────────────────────
//     barcodeImage = dataURL/base64 (optional). ไม่ส่งมาก็เป็นกล่องเปล่าไว้
// =================================================================
const drawBarcodeBox = (doc, editData, record, codes = {}) => {
  const left = MARGIN_X;
  const rowY = PAGE_H - 32; // ขอบบนของแถว barcode/QR
  const barcodeW = 85;
  const barcodeH = 12;
  const qrSize = 22;

  // ── Barcode (ซ้าย) + ข้อความใต้บาร์โค้ด ──
  if (codes.barcodeImg) {
    try {
      doc.addImage(codes.barcodeImg, "PNG", left, rowY, barcodeW, barcodeH);
    } catch (e) {
      // addImage ไม่สำเร็จ — ข้าม
    }
  }
  setFont(doc, "normal", 10);
  // แสดงเป็นช่องว่างแทน \r (ตัว \r ใช้เข้ารหัสบาร์โค้ดเท่านั้น)
  const barcodeText = (codes.barcodeValue || "").replace(/[\r\n]/g, " ");
  doc.text(barcodeText, left, rowY + barcodeH + 5);

  // ── QR สองตัวฝั่งขวา + label ใต้ภาพ ──
  const drawQr = (img, centerX, label) => {
    const x = centerX - qrSize / 2;
    if (img) {
      try {
        doc.addImage(img, "PNG", x, rowY, qrSize, qrSize);
      } catch (e) {
        // addImage ไม่สำเร็จ — ข้าม
      }
    }
    setFont(doc, "normal", 9);
    doc.text(label, centerX, rowY + qrSize + 5, { align: "center" });
  };

  drawQr(codes.qrBankImg, 130, "ชำระผ่านธนาคารช่องทางที่ 1");
  drawQr(codes.qrCounterImg, 174, "ชำระผ่านธนาคารช่องทางที่ 2");
};

// =================================================================
// ─── ฟังก์ชันหลัก : finaceBill (วาดทั้งหน้า A4) ──────────────────
// =================================================================
// เรียกใช้:
//   finaceBill(doc, data, editData, companyReceipt, record, codes)
//   codes = { barcodeImg, qrBankImg, qrCounterImg } จาก prepareCodes(data)
const finaceBill = (
  doc,
  data,
  editData = {},
  companyReceipt = {},
  record,
  codes = {},
) => {
  const ctxBase = { data, editData, companyReceipt, record };

  // ── ส่วนที่ 1 : ใบรับเงิน ──
  let y = drawReceiptSection(doc, data, editData, 12, companyReceipt, record);

  // ── เส้นแบ่ง + ส่วนที่ 2 : ลูกค้า (QR เคาน์เตอร์ ฝั่งขวา) ──
  y += 4;
  dashedLine(doc, y);
  y += 6;
  y = drawPayInSlip(doc, y, {
    ...ctxBase,
    sideLabel: "ส่วนของลูกค้า",
  });

  // ── เส้นแบ่ง + ส่วนที่ 3 : ธนาคาร (QR ธนาคาร ฝั่งขวา) ──
  y += 4;
  dashedLine(doc, y);
  y += 6;
  y = drawPayInSlip(doc, y, {
    ...ctxBase,
    sideLabel: "ส่วนของธนาคาร",
    qrImage: codes,
  });

  // ── barcode (กล่องรูปภาพล่างซ้าย) ──
  drawBarcodeBox(doc, editData, record, codes);

  return y;
};

// =================================================================
// ─── Main: ExportPDF ──────────────────────────────────────────────
// =================================================================
const ExportPDF = async (
  data,
  editData = {},
  companyInvoice = {},
  companyReceipt = {},
  record,
  roleId,
) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  doc.setFont(FONT, "normal");

  // ── Page 1: ใบกำกับภาษี (บน) + ใบเสร็จรับเงิน (ล่าง) ──
  drawInvoiceSection(doc, data, editData, 12, true, companyInvoice, record);

  doc.setLineDashPattern([2, 2], 0);
  doc.line(MARGIN_X, PAGE_H / 2, PAGE_W - MARGIN_X, PAGE_H / 2);
  doc.setLineDashPattern([], 0);

  drawInvoiceSection(
    doc,
    data,
    editData,
    PAGE_H / 2 + 5,
    false,
    companyInvoice,
    record,
  );

  // ── Page 2: บิลเงินสด ──
  doc.addPage();
  drawCashBill(doc, data, editData, companyReceipt, record);

  if (roleId === "6" || roleId === "1") {
    // ── Page 3: การเงิน ──
    doc.addPage();
    const codes = await prepareCodes(data); // เตรียม barcode + QR ก่อนวาด
    finaceBill(doc, data, editData, companyInvoice, record, codes);
  }
  return doc;
};

export default ExportPDF;
